import React, { useState, useEffect, createContext, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import axios from 'axios';
import Users from '../schemas/public/Users';
import { UserDataProp } from '../models/userDataProp';
import { Loader } from '../components/Loader';
import { getUser } from '../stores/User';
import { useAppSession } from '../hooks/useAppSession';
import useRouteActivity from '../hooks/useRouteActivity';
import { API_URL } from '../config';

export const UserContext = createContext<UserDataProp>({
    user: null,
    setUser: () => { },
    mergeUserData: () => { },
    refreshUser: async (tokenOverride?: string) => null,
    token: '',
    logout: () => { }
});

interface UserProviderProps {
    children?: React.ReactNode;
}

export function UserProvider({ children }: UserProviderProps): JSX.Element {
    const [loaded, setLoaded] = useState<boolean | false>(false);
    const history = useHistory();
    const { pathname } = useLocation();

    const [propData, setPropData] = useState<UserDataProp>({
        user: null,
        setUser: (userData: Users) => {
            setPropData(prev => ({ ...prev, user: userData }));
        },
        mergeUserData: (updatedFields: Partial<Users>) => {
            setPropData(prev => ({
                ...prev,
                user: prev.user ? { ...prev.user, ...updatedFields } : updatedFields as Users
            }));
        },
        token: '',
        refreshUser: async () => null,
        logout: () => { }
    });

    const { currentSession } = useAppSession(propData.token);

    // Track time spent on important routes/areas (games / album)
    useRouteActivity(propData.token);

    const logout = useCallback(async () => {
        const refreshToken = localStorage.getItem('refresh_token');
        if (refreshToken) {
            axios.post(`${API_URL}/auth/logout`, { refreshToken }).catch(() => {});
        }
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        setPropData(prev => ({ ...prev, token: '', user: null }));
        history.replace('/login');
    }, [history]);

    const refreshUser = useCallback(async (tokenOverride?: string): Promise<Users | null> => {
        // Use provided token or get the most current token from localStorage
        const currentToken = tokenOverride || localStorage.getItem('access_token') || '';
        if (!currentToken) {
            console.warn('No token available for refreshUser');
            return null;
        }
        try {
            const updatedUser: Users | null = await getUser(currentToken);
            if (updatedUser) {
                setPropData(prev => ({ ...prev, user: updatedUser, token: currentToken }));
                return updatedUser;
            } else {
                // If a token is found but no user
                // Do not update state if tokenOverride is provided (login case)
                // This allows the login function to handle the error itself
                if (!tokenOverride) {
                    // If this is an automatic refresh (no tokenOverride), log out
                    logout();
                } else {
                    // If this is a manual login, just set user to null to allow verification
                    setPropData(prev => ({ ...prev, user: null, token: currentToken }));
                }
                return null;
            }
        } catch (error) {
            console.error('Error refreshing user:', error);
            // In case of error, do not log out if it is a manual login.
            if (!tokenOverride) {
                logout();
            } else {
                setPropData(prev => ({ ...prev, user: null }));
            }
            return null;
        }
    }, [logout]);

    const setUser = useCallback((userData: Users) => {
        setPropData(prev => ({ ...prev, user: userData }));
    }, []);

    const mergeUserData = useCallback((updatedFields: Partial<Users>) => {
        setPropData(prev => ({
            ...prev,
            user: prev.user ? { ...prev.user, ...updatedFields } : updatedFields as Users
        }));
    }, []);

    // Update the propData with the latest functions
    useEffect(() => {
        setPropData(prev => ({
            ...prev,
            refreshUser,
            logout,
            setUser,
            mergeUserData
        }));
    }, [refreshUser, logout, setUser, mergeUserData]);

    useEffect(() => {
        const init = async () => {
            const localToken = localStorage.getItem('access_token') || '';
            
            if (!localToken) {
                if (
                    (pathname !== '/register' &&
                        pathname !== '/new-account' &&
                        pathname !== '/new-account-confirm' &&
                        pathname !== '/forgot-password' &&
                        pathname !== '/guide-utilisateurs' &&
                        pathname !== '/login' &&
                        pathname.slice(0, 7) !== '/login/') ||
                    pathname === '/login/me'
                ) {
                    history.replace('/login');
                    setLoaded(true);
                } else {
                    setLoaded(true);
                }
                return;
            }
            
            // If the token has changed or if we don't have a user yet
            if (localToken !== propData.token || !propData.user) {
                const updatedUser: Users | null = await getUser(localToken);
                if (!updatedUser && localToken !== '') {
                    logout();
                    return;
                }
                setPropData(prev => ({
                    ...prev,
                    token: localToken,
                    user: updatedUser
                }));
                setLoaded(true);
                if (updatedUser) {
                    if (
                        !updatedUser.firstName ||
                        !updatedUser.lastName ||
                        updatedUser.firstName === '' ||
                        updatedUser.lastName === ''
                    ) {
                        history.replace('/profile');
                    }
                }
            } else {
                setLoaded(true);
            }
        };
        init();
    }, [pathname, logout]);

    return loaded ? (
        <UserContext.Provider value={propData}>{children}</UserContext.Provider>
    ) : (
        <div
            style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                width: '100vw'
            }}>
            <Loader disableBg />
        </div>
    );
}
